/** Chat identity shared by the composer and its metadata-only recovery copy. */
function chatIdentity (location) {
  const match = location.pathname.match(/^\/(assistant|chat)(?:\/([^/]+))?/)
  return { route: match?.[1] || 'chat', chatId: match?.[2] || null }
}

/** Whether this route still has no saved chat identity. */
export function isUnsentChat (location) {
  return chatIdentity(location).chatId === null
}

/** Keep a fresh Focus launch from picking up an ordinary unsent draft. */
export function composerDraftKey (location) {
  const { route, chatId } = chatIdentity(location)
  const focusId = new URLSearchParams(location.search).get('focusDraftId')
  const scope = !chatId && focusId ? `:focus:${focusId}` : ''
  return `brainbox:draft:${route}:${chatId || 'new'}${scope}`
}

const KEY_PREFIX = 'brainbox:selected-tabs:'
/** A Focus launch's recovery copy outlives a restart, not the week */
const KEEP_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Removes Focus recovery copies, and the unsent Focus prompts they belong to,
 * once they are a week old, so a launch's tab titles and addresses do not sit
 * in storage for good. The launch this frame is showing is kept, however old
 * @param currentId - this frame's Focus launch, if any
 * @param storage - the frame's localStorage
 * @param now - the time to measure age from
 */
export function pruneFocusDrafts (currentId, storage, now = Date.now()) {
  try {
    // Read inside the try: a denied localStorage throws from its getter
    storage ??= localStorage
    const expired = []
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (!key?.startsWith(KEY_PREFIX) || key === `${KEY_PREFIX}${currentId}`) { continue }
      let savedAt
      try { savedAt = JSON.parse(storage.getItem(key))?.savedAt } catch { /* Unreadable copies go too. */ }
      if (!(savedAt > now - KEEP_MS)) { expired.push(key) }
    }
    for (const key of expired) {
      const focusId = key.slice(KEY_PREFIX.length)
      storage.removeItem(key)
      for (const route of ['chat', 'assistant']) { storage.removeItem(`brainbox:draft:${route}:new:focus:${focusId}`) }
    }
  } catch { /* Storage is optional. */ }
}

/**
 * Recovery copy of server-owned tab pills. No fetched content or uploads are
 * stored. Recovery contains persistent app controller references, never runtime
 * browser tab ids. The Focus launch and current chat identify its draft.
 */
export class SelectedTabDraft {
  constructor (focusId) {
    this.focusId = focusId
    this.generation = 0
    this.disabled = this.read()?.attachmentPolicy === 'automatic'
  }

  get key () { return `${KEY_PREFIX}${this.focusId}` }

  /** Read the optional metadata projection or reset tombstone. */
  read () {
    try { return JSON.parse(localStorage.getItem(this.key)) } catch { return null }
  }

  /** Observe a New Chat reset made by another surface of the same launch. */
  syncReset () {
    if (!this.disabled && this.read()?.attachmentPolicy === 'automatic') {
      this.disabled = true
      this.generation++
    }
  }

  /** Save the latest server projection, including an explicitly empty list. */
  save (encoded) {
    this.syncReset()
    try {
      const snapshot = encoded && JSON.parse(encoded)
      // The empty first render of a remount must not erase the recovery copy.
      if (this.disabled || !this.focusId || snapshot?.attachmentPolicy !== 'selected-tabs' ||
          snapshot.focusDraftId !== this.focusId) { return }
      localStorage.setItem(this.key, JSON.stringify({ ...snapshot, savedAt: Date.now() }))
    } catch { /* Storage is optional, like the text draft. */ }
  }

  /** Reset scope for an explicit New Chat and invalidate older requests. */
  clear () {
    // Only a Focus launch's scope changes, so only its pending replies are
    // stale; an ordinary chat keeps the initial context it is waiting for
    if (this.focusId) { this.generation++ }
    this.disabled = true
    // The URL remains across patches. Remember the explicit reset so a later
    // remount does not interpret that old URL as a still-selected Focus draft.
    try {
      if (this.focusId) { localStorage.setItem(this.key, JSON.stringify({ attachmentPolicy: 'automatic', savedAt: Date.now() })) }
    } catch { /* As above. */ }
  }

  /** Restore this launch and chat using persistent app references only. */
  contextReply (context, generation, location) {
    this.syncReset()
    if (generation !== this.generation) { return null }
    if (!this.focusId || this.disabled || context?.skillExecution) { return context }
    // The launch stays scoped after restart. Its durable references resolve to
    // current runtime tabs only when content is fetched; no unrelated fallback.
    const scoped = { ...context, attachmentPolicy: 'selected-tabs', focusDraftId: this.focusId, activeTab: null, selection: null }
    try {
      const snapshot = { ...this.read() }
      delete snapshot.savedAt
      if (snapshot.focusDraftId === this.focusId &&
          snapshot.chatId === chatIdentity(location).chatId) {
        return { ...scoped, selectedTabDraft: snapshot }
      }
    } catch { /* Missing or invalid recovery never broadens the selection. */ }
    return scoped
  }
}
